import {
	createContext,
	useContext,
	useMemo,
	useReducer,
	type ReactNode,
	type Dispatch,
	useCallback,
	useEffect,
} from "react";
import {
	korrekturTableReducer,
	type KorrekturTableState,
	type KorrekturTableAction,
} from "./korrekturTable.reducer";
import { useKorrekturStepAssignmentOperations } from "./korrekturStepAssignment.context";
import { useBulkAssignmentConfirmDialog } from "./bulkAssignmentDialog.context";
import type {
	StudentKlausurDetailedDTO,
	OrgaAdminUser,
} from "@/lib/orgaAdmin/orgaAdmin.types";

/**
 * The value provided by the KorrekturTableContext.
 * It includes the raw state, the dispatch function for actions,
 * and derived data that is computed from the state.
 */
interface KorrekturTableContextValue {
	state: KorrekturTableState;
	dispatch: Dispatch<KorrekturTableAction>;
	// Derived state (calculated from the raw state for performance)
	filteredStudentKlausuren: StudentKlausurDetailedDTO[];
	isStateMatchingAssignmentFilters: boolean;
	korrekturStepOptions: { value: string; label: string }[];
	bulkKorrektorOptions: { value: string; label: string }[];
	// Async actions that need access to other hooks
	handleBulkAssignment: (step: number, korrektorId: string) => Promise<void>;
	handleClearAllSelection: () => void;
}

const KorrekturTableContext = createContext<KorrekturTableContextValue | null>(
	null,
);

export interface KorrekturTableStateProviderProps {
	children: ReactNode;
	studentKlausuren: StudentKlausurDetailedDTO[];
	users: OrgaAdminUser[];
	klausurTitleMap: Map<string, string>;
	availableStepNumbers: number[];
	assignmentFilters?: {
		openBulkAssignment?: boolean;
		stepNumber?: number;
		korrektorId?: string;
		searchTerm?: string;
	} | null;
}

/**
 * Provides the state and dispatch function for the korrektur status table.
 * This component centralizes the table's logic, including state management,
 * derived data calculations, and side effects.
 */
export function KorrekturTableStateProvider({
	children,
	studentKlausuren,
	users,
	klausurTitleMap,
	availableStepNumbers,
	assignmentFilters,
}: KorrekturTableStateProviderProps) {
	// Initialize the state using the reducer.
	// The initial state is created from props passed to the provider.
	const initialState: KorrekturTableState = {
		studentKlausuren,
		users,
		klausurTitleMap,
		availableStepNumbers,
		searchTerm: assignmentFilters?.searchTerm || "",
		selectedStatus: "all",
		selectedStudentKlausurIds: new Set(),
		hasAppliedInitialFilters: false,
	};

	const [state, dispatch] = useReducer(korrekturTableReducer, initialState);

	// --- Other Hooks ---
	const { assignBulkKorrekturStepsToKorrektor, planBulkAssignment } =
		useKorrekturStepAssignmentOperations();
	const { openConfirmDialog } = useBulkAssignmentConfirmDialog();

	/**
	 * Filters the student klausuren based on the current search term and status filter.
	 */
	const filteredStudentKlausuren = useMemo(() => {
		return state.studentKlausuren.filter((klausur) => {
			const searchLower = state.searchTerm.toLowerCase();
			const matchesSearch =
				!state.searchTerm ||
				klausur.internalIdentifier.toLowerCase().includes(searchLower) ||
				(state.klausurTitleMap.get(klausur.klausurId) || "")
					.toLowerCase()
					.includes(searchLower) ||
				klausur.korrekturSteps.some((step) => {
					if (!step.korrektorId) return false;
					const assignedUser = state.users.find(
						(user) => user.id === step.korrektorId,
					);
					if (!assignedUser) return false;
					return (
						assignedUser.name?.toLowerCase().includes(searchLower) ||
						assignedUser.email.toLowerCase().includes(searchLower)
					);
				});

			if (state.selectedStatus === "all") {
				return matchesSearch;
			}
			const hasSelectedStatus = klausur.korrekturSteps.some(
				(step) => step.status === state.selectedStatus,
			);
			return matchesSearch && hasSelectedStatus;
		});
	}, [
		state.studentKlausuren,
		state.searchTerm,
		state.selectedStatus,
		state.users,
		state.klausurTitleMap,
	]);

	/**
	 * Checks if the current UI state matches the initial filters from deep-linking.
	 * This is used to show a "Vorbefüllt" (pre-filled) badge.
	 */
	const isStateMatchingAssignmentFilters = useMemo(() => {
		if (
			!assignmentFilters?.openBulkAssignment ||
			!state.hasAppliedInitialFilters
		) {
			return false;
		}
		const searchMatches =
			state.searchTerm === (assignmentFilters.searchTerm || "");
		const korrektorMatches =
			assignmentFilters.stepNumber !== undefined &&
			assignmentFilters.korrektorId !== undefined;
		return searchMatches && korrektorMatches;
	}, [assignmentFilters, state.hasAppliedInitialFilters, state.searchTerm]);

	const korrekturStepOptions = useMemo(() => {
		return state.availableStepNumbers.map((stepNr) => ({
			value: stepNr.toString(),
			label: `Korrektur-Schritt ${stepNr}`,
		}));
	}, [state.availableStepNumbers]);

	const bulkKorrektorOptions = useMemo(
		() => [
			{ value: "remove", label: "⚠️ Zuweisungen entfernen" },
			...state.users.map((user) => ({
				value: user.id,
				label: user.email,
			})),
		],
		[state.users],
	);

	// --- Side Effects ---

	/**
	 * This effect runs on mount to apply initial filters if they are provided
	 * via `assignmentFilters` props, typically from a deep link.
	 */
	useEffect(() => {
		if (
			assignmentFilters?.openBulkAssignment &&
			assignmentFilters?.stepNumber &&
			state.studentKlausuren.length > 0 &&
			!state.hasAppliedInitialFilters
		) {
			const searchTermToApply = assignmentFilters.searchTerm || "";
			dispatch({ type: "SET_SEARCH_TERM", payload: searchTermToApply });

			const studentKlausurIdsToSelect = new Set<string>();
			const searchLower = searchTermToApply.toLowerCase();
			state.studentKlausuren.forEach((klausur) => {
				const titleLower = (
					state.klausurTitleMap.get(klausur.klausurId) || ""
				).toLowerCase();
				const identifierLower = klausur.internalIdentifier.toLowerCase();
				const matchesSearch =
					titleLower.includes(searchLower) ||
					identifierLower.includes(searchLower);

				if (matchesSearch) {
					studentKlausurIdsToSelect.add(klausur.id);
				}
			});

			dispatch({
				type: "SET_SELECTED_KLAUSUR_IDS",
				payload: studentKlausurIdsToSelect,
			});
			dispatch({ type: "SET_HAS_APPLIED_INITIAL_FILTERS", payload: true });
		}
	}, [
		state.studentKlausuren,
		assignmentFilters,
		state.klausurTitleMap,
		state.hasAppliedInitialFilters,
	]);

	/**
	 * Performs the actual bulk assignment API call after validation and confirmation.
	 */
	const performBulkAssignment = useCallback(
		async (stepNumber: number, korrektorId: string) => {
			if (
				!stepNumber ||
				!korrektorId ||
				state.selectedStudentKlausurIds.size === 0
			) {
				return;
			}

			try {
				const plan = planBulkAssignment(
					stepNumber,
					Array.from(state.selectedStudentKlausurIds),
					korrektorId,
				);
				await assignBulkKorrekturStepsToKorrektor(
					plan.targetStepIds,
					plan.isRemoval ? "null" : korrektorId,
				);
			} catch (error) {
				console.error("Error in bulk assignment:", error);
			}
		},
		[
			state.selectedStudentKlausurIds,
			planBulkAssignment,
			assignBulkKorrekturStepsToKorrektor,
		],
	);

	/**
	 * Handles the user's intent to perform a bulk assignment. It plans the assignment,
	 * shows a confirmation dialog if necessary, and then calls `performBulkAssignment`.
	 */
	const handleBulkAssignment = useCallback(
		async (stepNumber: number, korrektorId: string) => {
			if (!korrektorId || state.selectedStudentKlausurIds.size === 0) {
				return;
			}
			const plan = planBulkAssignment(
				stepNumber,
				Array.from(state.selectedStudentKlausurIds),
				korrektorId,
			);
			if (!plan.isRemoval && !state.users.find((u) => u.id === korrektorId)) {
				console.warn("No valid korrektor selected");
				return;
			}
			const affectedDetails: string[] = plan.affectedAssignments.map((item) => {
				const assignedUser = state.users.find(
					(u) => u.id === item.currentKorrektorId,
				);
				return `${item.internalIdentifier}: ${
					assignedUser?.email || "Unbekannt"
				}`;
			});
			const duplicateDetails: string[] =
				!plan.isRemoval && plan.duplicateConflicts
					? plan.duplicateConflicts.map((dc) => {
							const steps = dc.conflictingStepNumbers
								.sort((a, b) => a - b)
								.join(", ");
							return `${dc.internalIdentifier}: Bereits in Schritt ${steps}`;
						})
					: [];
			if (
				affectedDetails.length > 0 ||
				plan.isRemoval ||
				duplicateDetails.length > 0
			) {
				const confirmed = await openConfirmDialog({
					isRemoval: plan.isRemoval,
					totalSelected: plan.validSelectionCount,
					affectedDetails,
					duplicateDetails,
					applicableCount: plan.targetStepIds.length,
					stepNumber,
				});
				if (confirmed) {
					await performBulkAssignment(stepNumber, korrektorId);
				}
			} else {
				await performBulkAssignment(stepNumber, korrektorId);
			}
		},
		[
			state.selectedStudentKlausurIds,
			planBulkAssignment,
			state.users,
			openConfirmDialog,
			performBulkAssignment,
		],
	);

	// Clears all selections and resets the bulk assignment UI.
	const handleClearAllSelection = useCallback(() => {
		dispatch({ type: "CLEAR_SELECTION" });
	}, []);

	// --- Final Context Value ---
	const value: KorrekturTableContextValue = {
		state,
		dispatch,
		filteredStudentKlausuren,
		isStateMatchingAssignmentFilters,
		korrekturStepOptions,
		bulkKorrektorOptions,
		handleBulkAssignment,
		handleClearAllSelection,
	};

	return (
		<KorrekturTableContext.Provider value={value}>
			{children}
		</KorrekturTableContext.Provider>
	);
}

export function useKorrekturTableState() {
	const context = useContext(KorrekturTableContext);
	if (!context) {
		throw new Error(
			"useKorrekturTableState must be used within a KorrekturTableStateProvider",
		);
	}
	return context;
}
